import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    addSharedMembershipParticipant,
    getStaffEntitlements,
    closeSharedMembershipPool,
    fetchSharePoolClosePreview,
    createGroupSharedMembershipPool,
    createSharedMembershipPool,
    getTemplateShareProvisioning,
    getSharedMembershipPoolByMemberTicket,
    getSharedMembershipPoolByMembership,
    removeSharedMembershipParticipant,
    retryTemplateShareProvisioning,
    updateSharedMembershipParticipantLimits,
} from './api';
import { sharedMembershipKeys } from './keys';
import {
    SharedMembershipPool,
    SharePoolScope,
    shouldFetchSharePoolByScope,
} from './types';

const fetchSharePoolByScope = (scope: SharePoolScope) =>
    scope.sourceType === 'MEMBERSHIP'
        ? getSharedMembershipPoolByMembership(scope.membershipId)
        : getSharedMembershipPoolByMemberTicket(scope.memberTicketId);

/** PT/그룹 공통 공유 풀 조회. 소스에 따라 조회 경로만 달라진다. */
export const useSharePoolByScope = ({
    scope,
    enabled,
}: {
    scope: SharePoolScope;
    enabled: boolean;
}) =>
    useQuery({
        queryKey: sharedMembershipKeys.poolByScope(scope),
        queryFn: () => fetchSharePoolByScope(scope),
        enabled: shouldFetchSharePoolByScope(enabled, scope),
        staleTime: 30_000,
        retry: false,
    });

/**
 * 종료 프리뷰. **종료 모달을 열 때만** 부른다.
 *
 * `staleTime: 0` 인 이유는 이 목록이 곧 `expectedItemIds` 가 되기 때문이다. 캐시된
 * 목록으로 종료를 보내면 그 사이 들어온 예약 때문에 서버가 SHARE_CLOSE_PLAN_CHANGED 로
 * 거절한다. 관리자가 본 것과 보내는 것이 같아야 하고, 둘 다 방금 것이어야 한다.
 *
 * `retry: false` — 구버전 서버에는 이 엔드포인트가 없어 404 다. 재시도해도 같고,
 * 호출부는 실패를 "정리 흐름을 열지 않는다"로 읽는다(오늘과 같은 차단).
 */
export const useSharePoolClosePreview = ({
    sharePoolId,
    enabled,
}: {
    sharePoolId: string;
    enabled: boolean;
}) =>
    useQuery({
        queryKey: sharedMembershipKeys.closePreview(sharePoolId),
        queryFn: () => fetchSharePoolClosePreview({ sharePoolId }),
        enabled: enabled && !!sharePoolId,
        staleTime: 0,
        cacheTime: 0,
        retry: false,
    });

export const useTemplateShareProvisioning = ({
    membershipId,
    enabled,
}: {
    membershipId: string;
    enabled: boolean;
}) =>
    useQuery({
        queryKey: sharedMembershipKeys.templateProvisioning(membershipId),
        queryFn: () => getTemplateShareProvisioning(membershipId),
        enabled: enabled && membershipId.trim().length > 0,
        staleTime: 10_000,
        retry: false,
    });

export const useRetryTemplateShareProvisioning = (membershipId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => retryTemplateShareProvisioning(membershipId),
        // 복구는 풀 생성으로 이어질 수 있어 provisioning·풀·예약 화면 권한까지 전부 갱신한다.
        // PROVISIONING → ACTIVE_HEALTHY 로 판정도 바뀌므로 수강권 상세까지 함께 무효화한다.
        onSuccess: () => invalidateShareLifecycleConsumers(queryClient),
    });
};

/**
 * 수강권 상세(`getMembershipForMemberQuery`)에는 서버가 매번 다시 계산한 `shareLifecycle`
 * (수정·재공유 가능 여부)이 함께 실려 온다. 그 판정의 근거가 **공유 풀의 상태**라서,
 * 풀을 만들거나 종료하거나 발급을 복구하면 상세도 같이 낡는다.
 *
 * 무효화하지 않으면 공유를 종료해도 화면은 종료 전 판정(canEditGeneralFields:false,
 * canReshare:false)을 그대로 들고 있어, 이번에 연 "종료 → 수정·재공유"가 새로고침
 * 전까지 동작하지 않는다. 참여자 추가·제거·한도 변경은 판정을 바꾸지 않아 제외한다.
 *
 * 키를 상수로 내보내는 모듈이 없어 다른 호출부와 같은 방식으로 접두어만 쓴다
 * (MemberMembershipManagePage·ModalMembershipUpdate도 같은 관용구다).
 */
const MEMBERSHIP_DETAIL_QUERY_PREFIX = ['getMembershipForMemberQuery'] as const;

const invalidateShareLifecycleConsumers = (queryClient: QueryClient) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: sharedMembershipKeys.all }),
        queryClient.invalidateQueries({ queryKey: MEMBERSHIP_DETAIL_QUERY_PREFIX }),
    ]);

/**
 * 공유 풀 변경 성공 공통 처리.
 * 1) 모든 변경 API는 갱신된 sharePool 전체를 돌려주므로 캐시에 먼저 심어 화면 지연을 없앤다.
 * 2) 이후 staffEntitlements(예약 화면)·provisioning까지 함께 새로고침되도록 최상위 키를 무효화한다.
 */
const useApplySharePoolMutationResult = (
    scope: SharePoolScope,
    /** 풀의 생사(생성·종료)가 바뀌는 변경만 수강권 상세까지 무효화한다. */
    options?: { invalidateMembershipDetail?: boolean },
) => {
    const queryClient = useQueryClient();
    const invalidateMembershipDetail = options?.invalidateMembershipDetail === true;
    return (pool: SharedMembershipPool | null) => {
        if (pool) {
            queryClient.setQueryData(sharedMembershipKeys.poolByScope(scope), pool);
        }
        return invalidateMembershipDetail
            ? invalidateShareLifecycleConsumers(queryClient)
            : queryClient.invalidateQueries({ queryKey: sharedMembershipKeys.all });
    };
};

export const useCreateSharedMembershipPool = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createSharedMembershipPool,
        onSuccess: (pool, variables) => {
            if (pool) {
                queryClient.setQueryData(
                    sharedMembershipKeys.poolByMembership(variables.sourceMembershipId),
                    pool,
                );
            }
            // 재공유도 이 경로다. 판정이 CLOSED_STABLE → ACTIVE_HEALTHY 로 바뀐다.
            return invalidateShareLifecycleConsumers(queryClient);
        },
    });
};

export const useCreateGroupSharedMembershipPool = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createGroupSharedMembershipPool,
        onSuccess: (pool, variables) => {
            if (pool) {
                queryClient.setQueryData(
                    sharedMembershipKeys.poolByMemberTicket(variables.sourceMemberTicketId),
                    pool,
                );
            }
            return invalidateShareLifecycleConsumers(queryClient);
        },
    });
};

export const useAddSharedMembershipParticipant = (scope: SharePoolScope) => {
    const applyResult = useApplySharePoolMutationResult(scope);
    return useMutation({
        mutationFn: addSharedMembershipParticipant,
        onSuccess: (pool) => applyResult(pool),
    });
};

export const useRemoveSharedMembershipParticipant = (scope: SharePoolScope) => {
    const applyResult = useApplySharePoolMutationResult(scope);
    return useMutation({
        mutationFn: removeSharedMembershipParticipant,
        onSuccess: (pool) => applyResult(pool),
    });
};

export const useUpdateSharedMembershipParticipantLimits = (scope: SharePoolScope) => {
    const applyResult = useApplySharePoolMutationResult(scope);
    return useMutation({
        mutationFn: updateSharedMembershipParticipantLimits,
        onSuccess: (pool) => applyResult(pool),
    });
};

export const useCloseSharedMembershipPool = (scope: SharePoolScope) => {
    // 종료는 ACTIVE_* → CLOSED_STABLE 로 판정을 바꾼다. 상세를 함께 갱신하지 않으면
    // 바로 이어지는 수정·재공유가 낡은 판정에 막힌다.
    const applyResult = useApplySharePoolMutationResult(scope, {
        invalidateMembershipDetail: true,
    });
    return useMutation({
        mutationFn: closeSharedMembershipPool,
        // 종료 응답은 { pool, cleanup } 이다. 캐시에는 풀만 심고, cleanup(실제 취소
        // 결과)은 호출부가 mutateAsync 반환값으로 받아 안내에 쓴다.
        onSuccess: (result) => applyResult(result?.pool ?? null),
    });
};

/**
 * 회원이 공유받아 쓰는 수강권 목록.
 * 참여자에게는 Membership 문서가 없어 본인 소유 목록 API로는 절대 나오지 않는다.
 * 프로필당 한 번만 호출하며, 담당 강사로 좁히지 않아 다른 강사의 공유도 함께 보인다.
 */
/** 그룹 공유 사용 권리. PT 훅과 같은 엔드포인트라 캐시만 분리한다. */
export const useMemberSharedGroupEntitlements = ({
    memberId,
    centerId,
    enabled,
}: {
    memberId: string;
    centerId: string;
    enabled: boolean;
}) =>
    useQuery({
        /**
         * PT용 훅과 같은 캐시 키를 쓴다. 서버가 두 배열을 한 응답에 담아 주므로
         * 키를 나누면 같은 요청이 두 번 나간다 — 이 엔드포인트는 schedules 집계를 돌아
         * 비싼 축이라 회원 프로필 한 번 여는 데 부하가 두 배가 됐다.
         */
        queryKey: sharedMembershipKeys.staffEntitlements(centerId, '', memberId),
        queryFn: () => getStaffEntitlements({ memberId, centerId }),
        select: (data) => data.groupEntitlements,
        // 호출부가 location.state에서 memberId를 받는 경로가 있어 undefined로 들어올 수 있다.
        // 그대로 .trim()하면 페이지 전체가 흰 화면이 된다.
        enabled: enabled && String(memberId || '').trim() !== '' && String(centerId || '').trim() !== '',
        staleTime: 30_000,
        retry: false,
    });

export const useMemberSharedEntitlements = ({
    memberId,
    centerId,
    enabled,
}: {
    memberId: string;
    centerId: string;
    enabled: boolean;
}) =>
    useQuery({
        // 그룹용 훅과 같은 키·같은 요청을 공유하고 필요한 배열만 고른다.
        queryKey: sharedMembershipKeys.staffEntitlements(centerId, '', memberId),
        queryFn: () => getStaffEntitlements({ memberId, centerId }),
        select: (data) => data.entitlements,
        enabled: enabled && String(memberId || '').trim() !== '' && String(centerId || '').trim() !== '',
        staleTime: 30_000,
        retry: false,
    });
